import React, { forwardRef, useMemo, useState } from "react";
import { Button, Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import EntityStatus from "go-list/core/components/Table/components/EntityStatus";
import { selectOrganization } from "go-security/services/organizations/selectors";
import useDashboardContext from "../../services/hooks/useDashboardContext";
import { useDashboardDraggable } from "../../services/hooks/useDashboardDraggable";
import { DashboardGridItem, DraggableComponentProps } from "../../services/types";
import { getParsedAlerts } from "../../utils/alerts";
import DashboardAlertItem from "./DashboardAlertItem";
import DashboardAlertsModal from "./DashboardAlertsModal";

export interface DashboardAlert {
	status: string;
	link: string | undefined;
	title: string;
}

const DraggableDashboardAlerts = forwardRef<HTMLDivElement, DraggableComponentProps>(
	({ style, className, onMouseDown, onMouseUp, onTouchEnd, setNonMobileLayout }, ref): JSX.Element => {
		const { state, isEditMode } = useDashboardContext();
		const organization = useSelector(selectOrganization);
		const [showMoreAlertsModal, setShowMoreAlertsModal] = useState<boolean>(false);
		const [alerts, setAlerts] = useState<DashboardAlert[]>([]);
		const { t } = useTranslation();
		const dashboardAlerts = state?.data?.alerts;
		const refChildren = ref && "current" in ref ? ref.current?.children?.[0]?.children : undefined;
		const { gridItemZIndex, cursorType, onMouseEnter, onMouseLeave, onCustomMouseDown, onCustomMouseUp } =
			useDashboardDraggable(DashboardGridItem.ALERTS, setNonMobileLayout, refChildren, onMouseDown, onMouseUp);

		const renderAlerts = useMemo(() => {
			return getParsedAlerts(setAlerts, t, organization.id, dashboardAlerts).map((alert) => (
				<DashboardAlertItem
					key={alert.title}
					status={alert.status}
					link={alert.link}
					title={alert.title}
					isModalItem={false}
				/>
			));
		}, [dashboardAlerts, t, organization.id]);

		const getAlertsStatus = () => {
			const statuses = alerts.map((alert) => alert.status);
			if (statuses.includes("DANGER")) return "DISABLED";
			if (statuses.includes("WARNING")) return "WARNING";
			return "ENABLED";
		};

		const getAlertsCardBorderStyles = () => {
			const status = getAlertsStatus();
			switch (status) {
				case "ENABLED":
					return "";
				case "DISABLED":
					return "border-danger";
				case "WARNING":
					return "border-warning";
			}
		};

		const getTotalAmountOfAlerts = () => alerts.length;

		if (getTotalAmountOfAlerts() === 0) return <></>;

		return (
			<>
				<Card
					className={`${className} ${
						isEditMode ? "dashboard-edit-mode-element-active" : ""
					} dashboard-alerts-card ${isEditMode ? "" : getAlertsCardBorderStyles()}`}
					ref={ref}
					style={{ ...style, zIndex: gridItemZIndex, cursor: cursorType }}
					onMouseDown={onCustomMouseDown}
					onMouseUp={onCustomMouseUp}
					onTouchEnd={onTouchEnd}
					onMouseEnter={onMouseEnter}
					onMouseLeave={onMouseLeave}
				>
					<Card.Body>
						<h5>
							{t("modules.dashboard.header.dashboard_alerts_modal.title")}
							<EntityStatus status={getAlertsStatus()} />
						</h5>
						<>
							{renderAlerts.filter((_, index) => index < 3)}
							{getTotalAmountOfAlerts() > 3 && (
								<Button
									variant="add w-100 mt-2"
									onClick={() => setShowMoreAlertsModal(true)}
									disabled={isEditMode}
								>
									{t("common.action.show_all", { ns: "lib" })}
									<strong className="ms-1">{getTotalAmountOfAlerts()}</strong>
								</Button>
							)}
						</>
					</Card.Body>
				</Card>
				{showMoreAlertsModal && (
					<DashboardAlertsModal
						isShown={showMoreAlertsModal}
						onHide={() => setShowMoreAlertsModal(false)}
						getAlertsStatus={getAlertsStatus}
						alerts={alerts}
					/>
				)}
			</>
		);
	}
);

DraggableDashboardAlerts.displayName = "DraggableDashboardAlerts";

export default DraggableDashboardAlerts;
